/**
 * Verificação humana dos documentos de suporte via `DocumentReference.attester`.
 *
 * Cada aprovação grava **dois** attesters (mode `professional`):
 * - `party`: `PractitionerRole/{id}` — profissional que verificou
 * - `party`: `Organization/{oids.cnpj}-{organizationId}` — organização responsável
 *
 * PATCH via `PatchEHRPoster` com `operation: "add"` e path `/attester` ou `/attester/-`.
 * Leitura de “aprovado” considera o attester da **Organization** (gate do marketplace).
 * @module documentAttestationService
 */

import dayjs from "dayjs";
import { oids } from "@/configs/Settings";
import { PatchEHRPoster } from "@/services/fhir/patientsummary/PatchEHRPoster";

import { PATIENT_SUPPORTING_DOCUMENT_CATEGORIES } from "../types";
import type { PatientSupportingDocumentTypeId } from "../types";
import {
  DOCUMENT_TRANSACTION_ID_EXTENSION_URL,
  DOCUMENT_VALID_UNTIL_EXTENSION_URL,
  buildValidUntilExtension,
  extractDocumentTransactionId,
  extractDocumentValidUntil,
  extractValidUntilFromExtensions,
  isDocumentValidityExpired,
  isDocumentValidityExpiredDate,
  isLegacySupportingDocument,
  mergeDocumentValidityExtensions,
  type DocumentExtensionLike,
} from "../helpers/documentExtensions";

export {
  DOCUMENT_TRANSACTION_ID_EXTENSION_URL,
  DOCUMENT_VALID_UNTIL_EXTENSION_URL,
  extractDocumentTransactionId,
  extractDocumentValidUntil,
  isDocumentValidityExpired,
  isLegacySupportingDocument,
};

const PROFESSIONAL_ATTESTATION_MODE = "professional";
export const DOCUMENT_REJECTION_STATUS_CODE = "rejected";
/** docStatus aceito pelo EHRPOSTER (mesmo valor do cancelamento do paciente). */
export const DOCUMENT_ARCHIVED_STATUS_CODE = "cancelled";
/** Motivo da recusa — `DocumentReference.extension` (campo FHIR nativo). */
export const DOCUMENT_REJECTION_REASON_EXTENSION_URL =
  "https://fluxmed.com.br/fhir/extension/supporting-document/rejection-reason";
/** Marca recusa feita pelo profissional (diferencia do cancelamento do paciente). */
export const DOCUMENT_REJECTION_SOURCE_EXTENSION_URL =
  "https://fluxmed.com.br/fhir/extension/supporting-document/rejection-source";
/**
 * Organização que reprovou (multi-loja): valueString `Organization/{oids.cnpj}-{cnpj}`.
 * Não usa docStatus global — outras lojas permanecem independentes.
 */
export const DOCUMENT_REJECTION_ORGANIZATION_EXTENSION_URL =
  "https://fluxmed.com.br/fhir/extension/supporting-document/rejection-organization";
/** Valor legado — recusas antigas usavam só `"professional"`. */
export const DOCUMENT_PROFESSIONAL_REJECTION_SOURCE = "professional";

export function buildProfessionalRejectionSourceValue(
  practitionerRoleId: string,
): string {
  const roleId = practitionerRoleId.trim();
  if (!roleId) {
    throw new Error("Vínculo profissional não identificado para reprovação.");
  }
  return `PractitionerRole/${roleId}`;
}

function isProfessionalRejectionSourceValue(value: string | undefined): boolean {
  const normalized = value?.trim();
  if (!normalized) return false;
  if (normalized === DOCUMENT_PROFESSIONAL_REJECTION_SOURCE) return true;
  return normalized.startsWith("PractitionerRole/");
}

/** Lê quem reprovou — `PractitionerRole/{id}` ou legado `"professional"`. */
export function extractProfessionalRejectionSource(
  docRef: AttestableDocumentLike | undefined,
): string | undefined {
  const value = docRef?.extension
    ?.find((ext) => ext.url === DOCUMENT_REJECTION_SOURCE_EXTENSION_URL)
    ?.valueString?.trim();
  if (!value || !isProfessionalRejectionSourceValue(value)) return undefined;
  return value;
}

/** CodeableConcept ou string legada em `DocumentReference.docStatus`. */
export type DocumentStatusLike =
  | string
  | {
      coding?: Array<{ system?: string; code?: string }>;
      text?: string;
    };

/** Estrutura mínima (estrutural) de um item de `DocumentReference.attester`. */
export interface AttesterEntryLike {
  mode?: { coding?: Array<{ code?: string }> };
  time?: string;
  party?: { reference?: string };
  extension?: DocumentExtensionLike[];
}

/** Estrutura mínima de um DocumentReference para leitura de attestation. */
export interface AttestableDocumentLike {
  id?: string;
  attester?: AttesterEntryLike[];
  docStatus?: DocumentStatusLike;
  extension?: DocumentExtensionLike[];
}

export type DocumentVerificationStatus =
  | "none"
  | "pending"
  | "approved"
  | "rejected";

/** Resultado da leitura de verificação (attester + docStatus). */
export interface DocumentVerificationInfo {
  status: DocumentVerificationStatus;
  attestedAt?: string;
  attestedByReference?: string;
  /** Motivo legível — `DocumentReference.extension` (valueString). */
  rejectionReason?: string;
  /** Quem reprovou — `PractitionerRole/{id}` na extension rejection-source. */
  rejectedByReference?: string;
  rejectedAt?: string;
  /** Extension supporting-document/valid-until no attester da Organization. */
  validUntil?: string;
  /** Extension supporting-document/transaction-id. */
  transactionId?: string;
  /**
   * true só quando esta org tem valid-until e ela já passou.
   * Validade de outra clínica não conta.
   */
  isExpired?: boolean;
}

/** Resultado da leitura de attestation de um documento. */
export interface DocumentAttestationInfo {
  isAttested: boolean;
  attestedAt?: string;
  attestedByReference?: string;
}

/** Resumo das 4 categorias — usado no gate do marketplace do paciente. */
export interface SupportingDocumentsAttestationSummary {
  isFullyAttested: boolean;
  pendingCategories: PatientSupportingDocumentTypeId[];
  /** Attestation mais recente dentre as categorias verificadas. */
  attestedAt?: string;
}

/** Entrada aceita pelo resumo: doc FHIR cru ou campos já mapeados na listagem. */
export interface AttestationSummaryInput {
  attester?: AttesterEntryLike[];
  docStatus?: DocumentStatusLike;
  isAttested?: boolean;
  attestedAt?: string;
}

export interface AttestSupportingDocumentParams {
  documentReferenceId: string;
  /** CNPJ da organização sem prefixo OID (padrão do legado). */
  organizationId: string;
  /** Id composto do PractitionerRole (sem prefixo `PractitionerRole/`). */
  practitionerRoleId: string;
  /** Attester atual do DocumentReference — garante idempotência e o path correto. */
  currentAttester?: AttesterEntryLike[];
  /** Extensions atuais — merge de transaction-id; valid-until da raiz é removido. */
  currentExtension?: DocumentExtensionLike[];
  /** Data de validade obrigatória na aprovação (YYYY-MM-DD). */
  validUntil: string;
  /** UUID da transação — receita e ANVISA do mesmo par. */
  transactionId?: string;
}

export interface AttestSupportingDocumentResult {
  documentReferenceId: string;
  /** true quando a org já havia atestado — nenhum PATCH é disparado. */
  alreadyAttested: boolean;
}

export interface AttestAllSupportingDocumentsParams {
  organizationId: string;
  practitionerRoleId: string;
  validUntil: string;
  transactionId?: string;
  documents: Array<{
    documentReferenceId: string;
    currentAttester?: AttesterEntryLike[];
    currentExtension?: DocumentExtensionLike[];
    transactionId?: string;
  }>;
}

export interface RevokeDocumentApprovalParams {
  documentReferenceId: string;
  organizationId: string;
  currentAttester?: AttesterEntryLike[];
}

export interface RevokeDocumentRejectionParams {
  documentReferenceId: string;
  organizationId: string;
  currentExtension?: DocumentExtensionLike[];
}

export interface AttestAllSupportingDocumentsResult {
  attested: string[];
  alreadyAttested: string[];
  /** Ids que falharam no PATCH — tratamento de erro parcial fica na UI. */
  failed: string[];
}

export interface RejectSupportingDocumentParams {
  documentReferenceId: string;
  /** Motivo da recusa — persistido em `DocumentReference.extension`. */
  reason: string;
  /** Id composto do PractitionerRole (sem prefixo `PractitionerRole/`). */
  practitionerRoleId: string;
  /** CNPJ da loja — reprovação scoped (não invalida outras orgs). */
  organizationId: string;
  /** Extensions atuais — define path `/extension` vs `/extension/-`. */
  currentExtension?: DocumentExtensionLike[];
}

export interface RejectSupportingDocumentResult {
  documentReferenceId: string;
}

/** Extrai o code de docStatus (string legada ou CodeableConcept). */
export function getDocumentStatusCode(
  docStatus: DocumentStatusLike | undefined,
): string | undefined {
  if (docStatus == null) return undefined;
  if (typeof docStatus === "string") return docStatus.toLowerCase();
  return docStatus.coding?.[0]?.code?.toLowerCase();
}

/** Texto legível de docStatus — usado como motivo de recusa (FHIR CodeableConcept.text). */
export function getDocumentStatusText(
  docStatus: DocumentStatusLike | undefined,
): string | undefined {
  if (docStatus == null || typeof docStatus === "string") return undefined;
  return docStatus.text?.trim() || undefined;
}

export function isRejectedDocumentStatus(
  docStatus: DocumentStatusLike | undefined,
): boolean {
  return getDocumentStatusCode(docStatus) === DOCUMENT_REJECTION_STATUS_CODE;
}

function buildOrganizationRejectionReference(organizationId: string): string {
  return `Organization/${oids.cnpj}-${organizationId.trim()}`;
}

function hasOrgScopedRejectionExtensions(
  docRef: AttestableDocumentLike | undefined,
): boolean {
  return Boolean(
    docRef?.extension?.some(
      (ext) => ext.url === DOCUMENT_REJECTION_ORGANIZATION_EXTENSION_URL,
    ),
  );
}

/** Recusa profissional legada (global): docStatus rejected ou source sem org scoped.
 * Preferir `isDocumentRejectedByOrganization` no ecommerce multi-loja. */
export function isProfessionalRejectionDocument(
  docRef: AttestableDocumentLike | undefined,
): boolean {
  if (!docRef) return false;
  if (isRejectedDocumentStatus(docRef.docStatus)) return true;
  // Novos rejects só usam rejection-organization — sem marcar global
  if (hasOrgScopedRejectionExtensions(docRef)) return false;
  return Boolean(
    docRef.extension?.some(
      (ext) =>
        ext.url === DOCUMENT_REJECTION_SOURCE_EXTENSION_URL &&
        isProfessionalRejectionSourceValue(ext.valueString),
    ),
  );
}

/**
 * Recusa profissional visível ao paciente (legado ou extension com org scoped).
 * Usado na listagem do Patient Summary — não depende de organizationId da sessão.
 */
export function hasProfessionalDocumentRejection(
  docRef: AttestableDocumentLike | undefined,
): boolean {
  if (!docRef) return false;
  if (isRejectedDocumentStatus(docRef.docStatus)) return true;

  const reason = extractRejectionReason(docRef);
  if (!reason) return false;

  if (hasOrgScopedRejectionExtensions(docRef)) return true;

  return Boolean(
    docRef.extension?.some(
      (ext) =>
        ext.url === DOCUMENT_REJECTION_SOURCE_EXTENSION_URL &&
        isProfessionalRejectionSourceValue(ext.valueString),
    ),
  );
}

/** Recusa desta organização (extension rejection-organization) ou legado global. */
export function isDocumentRejectedByOrganization(
  docRef: AttestableDocumentLike | undefined,
  organizationId?: string,
): boolean {
  if (!docRef) return false;
  const orgId = organizationId?.trim();
  if (orgId) {
    const orgRef = buildOrganizationRejectionReference(orgId);
    const scoped = docRef.extension?.some(
      (ext) =>
        ext.url === DOCUMENT_REJECTION_ORGANIZATION_EXTENSION_URL &&
        (ext.valueString?.trim() === orgRef ||
          ext.valueString?.includes(orgId)),
    );
    if (scoped) return true;
  }
  // Legado: sem extension de org → vale para todas as lojas
  return isProfessionalRejectionDocument(docRef);
}

export function isCancelledDocumentStatus(
  docStatus: DocumentStatusLike | undefined,
): boolean {
  return getDocumentStatusCode(docStatus) === "cancelled";
}

/** Lê o motivo da recusa de `DocumentReference.extension` (fallback: docStatus.text legado). */
export function extractRejectionReason(
  docRef: AttestableDocumentLike | undefined,
): string | undefined {
  const fromExtension = docRef?.extension
    ?.find((ext) => ext.url === DOCUMENT_REJECTION_REASON_EXTENSION_URL)
    ?.valueString?.trim();
  if (fromExtension) return fromExtension;
  return getDocumentStatusText(docRef?.docStatus);
}

function buildAttesterBase(): Pick<AttesterEntryLike, "mode" | "time"> {
  return {
    mode: {
      coding: [
        {
          code: PROFESSIONAL_ATTESTATION_MODE,
        },
      ],
    },
    time: dayjs().toISOString(),
  };
}

/** Attester da organização ecommerce que validou o documento. */
export function buildOrganizationAttesterEntry(
  organizationId: string,
  validUntil?: string,
): AttesterEntryLike {
  const validUntilDate = validUntil?.trim();
  return {
    ...buildAttesterBase(),
    party: {
      reference: `Organization/${oids.cnpj}-${organizationId}`,
    },
    ...(validUntilDate
      ? { extension: [buildValidUntilExtension(validUntilDate)] }
      : {}),
  };
}

/** Attester do vínculo profissional (PractitionerRole) que verificou o documento. */
export function buildPractitionerRoleAttesterEntry(
  practitionerRoleId: string,
): AttesterEntryLike {
  const roleId = practitionerRoleId.trim();
  return {
    ...buildAttesterBase(),
    party: {
      reference: `PractitionerRole/${roleId}`,
    },
  };
}

/**
 * @deprecated Preferir `buildPractitionerRoleAttesterEntry` + `buildOrganizationAttesterEntry`.
 * Mantido por compatibilidade de export — retorna attester de PractitionerRole.
 */
export function buildProfessionalAttesterEntry(
  practitionerRoleId: string,
): AttesterEntryLike {
  return buildPractitionerRoleAttesterEntry(practitionerRoleId);
}

function isProfessionalAttesterEntry(entry: AttesterEntryLike): boolean {
  return Boolean(
    entry?.mode?.coding?.some(
      (coding) => coding?.code === PROFESSIONAL_ATTESTATION_MODE,
    ),
  );
}

/**
 * Localiza o attester profissional da organização (gate de verificação).
 * Docs legados com attester só de Organization continuam válidos.
 */
function findOrganizationAttester(
  attester: AttesterEntryLike[] | undefined,
  organizationId?: string,
): AttesterEntryLike | undefined {
  const orgId = organizationId?.trim();
  return attester?.find((entry) => {
    if (!isProfessionalAttesterEntry(entry)) return false;
    const reference = entry.party?.reference ?? "";
    if (!reference.includes("Organization/")) return false;
    if (!orgId) return true;
    return reference.includes(orgId);
  });
}

/**
 * Validade só da organização da sessão.
 * Sem attester desta org → undefined (aprovação de outra clínica não vaza).
 * Attester desta org sem extension → fallback na raiz (aprovações antigas).
 */
export function extractOrganizationValidUntil(
  docRef: AttestableDocumentLike | undefined,
  organizationId?: string,
): string | undefined {
  const entry = findOrganizationAttester(docRef?.attester, organizationId);
  if (!entry) return undefined;
  return (
    extractValidUntilFromExtensions(entry.extension) ||
    extractDocumentValidUntil(docRef)
  );
}

function findPractitionerRoleAttester(
  attester: AttesterEntryLike[] | undefined,
  practitionerRoleId?: string,
): AttesterEntryLike | undefined {
  const roleId = practitionerRoleId?.trim();
  return attester?.find((entry) => {
    if (!isProfessionalAttesterEntry(entry)) return false;
    const reference = entry.party?.reference ?? "";
    if (!reference.includes("PractitionerRole/")) return false;
    if (!roleId) return true;
    return reference.includes(roleId);
  });
}

/**
 * Documento verificado pela organização (gate do marketplace).
 * Docs antigos com attester só de Organization continuam válidos.
 */
export function isDocumentAttestedByOrganization(
  docRef: AttestableDocumentLike | undefined,
  organizationId?: string,
): boolean {
  return Boolean(findOrganizationAttester(docRef?.attester, organizationId));
}

/** Leitura completa (status + data + party) do attester da organização. */
export function extractDocumentAttestation(
  docRef: AttestableDocumentLike | undefined,
  organizationId?: string,
): DocumentAttestationInfo {
  const entry = findOrganizationAttester(docRef?.attester, organizationId);
  if (!entry) {
    return { isAttested: false };
  }
  return {
    isAttested: true,
    attestedAt: entry.time,
    attestedByReference: entry.party?.reference,
  };
}

/**
 * Verificação por organização da sessão.
 * Aprovação (attester desta org) prevalece sobre recusa anterior da mesma org.
 * Recusa scoped não afeta outras lojas.
 */
export function extractDocumentVerification(
  docRef: AttestableDocumentLike | undefined,
  organizationId?: string,
): DocumentVerificationInfo {
  if (!docRef) return { status: "none" };

  const validUntil = extractOrganizationValidUntil(docRef, organizationId);
  const transactionId = extractDocumentTransactionId(docRef);
  const isExpired = isDocumentValidityExpiredDate(validUntil);

  const attestation = extractDocumentAttestation(docRef, organizationId);
  if (attestation.isAttested) {
    return {
      status: "approved",
      attestedAt: attestation.attestedAt,
      attestedByReference: attestation.attestedByReference,
      validUntil,
      transactionId,
      isExpired,
    };
  }

  if (isDocumentRejectedByOrganization(docRef, organizationId)) {
    return {
      status: "rejected",
      rejectionReason: extractRejectionReason(docRef),
      rejectedByReference: extractProfessionalRejectionSource(docRef),
      rejectedAt: undefined,
      validUntil,
      transactionId,
      isExpired,
    };
  }

  return { status: "pending", validUntil, transactionId, isExpired };
}

function resolveSummaryInput(
  input: AttestationSummaryInput | undefined,
  organizationId?: string,
): DocumentAttestationInfo {
  if (!input) return { isAttested: false };
  if (input.docStatus != null || input.attester) {
    const verification = extractDocumentVerification(input, organizationId);
    if (verification.status === "approved") {
      return {
        isAttested: true,
        attestedAt: verification.attestedAt,
        attestedByReference: verification.attestedByReference,
      };
    }
    return { isAttested: false };
  }
  return {
    isAttested: Boolean(input.isAttested),
    attestedAt: input.attestedAt,
  };
}

/**
 * Resumo das 4 categorias: todas verificadas, pendentes e data mais recente.
 * Aceita o documento ativo mais recente de cada categoria (FHIR cru ou mapeado).
 */
export function areAllSupportingDocumentsAttested(
  activeDocsByType: Partial<
    Record<PatientSupportingDocumentTypeId, AttestationSummaryInput | undefined>
  >,
  organizationId?: string,
): SupportingDocumentsAttestationSummary {
  const typeIds = Object.keys(
    PATIENT_SUPPORTING_DOCUMENT_CATEGORIES,
  ) as PatientSupportingDocumentTypeId[];

  const pendingCategories: PatientSupportingDocumentTypeId[] = [];
  let attestedAt: string | undefined;

  for (const typeId of typeIds) {
    const input = activeDocsByType[typeId];
    if (!input) {
      pendingCategories.push(typeId);
      continue;
    }

    if (input.attester) {
      const verification = extractDocumentVerification(input, organizationId);
      if (verification.status === "rejected") {
        pendingCategories.push(typeId);
        continue;
      }
      if (verification.status === "approved") {
        if (
          verification.attestedAt &&
          (!attestedAt ||
            Date.parse(verification.attestedAt) > Date.parse(attestedAt))
        ) {
          attestedAt = verification.attestedAt;
        }
        continue;
      }
    }

    const info = resolveSummaryInput(input, organizationId);
    if (!info.isAttested) {
      pendingCategories.push(typeId);
      continue;
    }
    if (
      info.attestedAt &&
      (!attestedAt || Date.parse(info.attestedAt) > Date.parse(attestedAt))
    ) {
      attestedAt = info.attestedAt;
    }
  }

  const isFullyAttested = pendingCategories.length === 0;
  return {
    isFullyAttested,
    pendingCategories,
    attestedAt: isFullyAttested ? attestedAt : undefined,
  };
}

/**
 * Grava os attesters (PractitionerRole + Organization) no DocumentReference via PATCH.
 * Idempotente: só adiciona entradas ausentes.
 */
/**
 * Persiste `transaction-id` na raiz e remove `valid-until` global
 * (validade vive no attester da org).
 */
async function persistDocumentValidityExtensions(
  documentReferenceId: string,
  currentExtension: DocumentExtensionLike[] | undefined,
  transactionId?: string,
): Promise<void> {
  const merged = mergeDocumentValidityExtensions(currentExtension, {
    transactionId,
  });
  const hasExisting = Boolean(currentExtension?.length);
  if (!hasExisting && !merged.length) return;
  await PatchEHRPoster({
    resourceType: "DocumentReference",
    resourceId: documentReferenceId,
    operation: hasExisting ? "replace" : "add",
    path: "/extension",
    value: merged,
    applicationAuth: true,
  });
}

/**
 * Grava os attesters (PractitionerRole + Organization) no DocumentReference via PATCH.
 * Também persiste `valid-until` no attester da Organization e `transaction-id` na raiz.
 * Idempotente: só adiciona entradas de attester ausentes.
 */
export async function attestSupportingDocument(
  params: AttestSupportingDocumentParams,
): Promise<AttestSupportingDocumentResult> {
  const documentReferenceId = params.documentReferenceId?.trim();
  const organizationId = params.organizationId?.trim();
  const practitionerRoleId = params.practitionerRoleId?.trim();
  const validUntil = params.validUntil?.trim();

  if (!documentReferenceId) {
    throw new Error("Documento não identificado para verificação.");
  }
  if (!organizationId) {
    throw new Error("Organização não identificada para verificação.");
  }
  if (!practitionerRoleId) {
    throw new Error("Vínculo profissional não identificado para verificação.");
  }
  if (!validUntil || !dayjs(validUntil).isValid()) {
    throw new Error("Informe a data de validade do documento.");
  }
  if (dayjs(validUntil).isBefore(dayjs(), "day")) {
    throw new Error("A validade deve ser hoje ou uma data futura.");
  }

  const currentAttester = params.currentAttester ?? [];
  const hasOrganizationAttester = Boolean(
    findOrganizationAttester(currentAttester, organizationId),
  );
  const hasPractitionerRoleAttester = Boolean(
    findPractitionerRoleAttester(currentAttester, practitionerRoleId),
  );
  const organizationAttester = buildOrganizationAttesterEntry(
    organizationId,
    validUntil,
  );

  const alreadyAttested =
    hasOrganizationAttester && hasPractitionerRoleAttester;

  if (alreadyAttested) {
    const nextAttester = currentAttester.map((entry) =>
      isOrganizationAttesterEntry(entry, organizationId)
        ? organizationAttester
        : entry,
    );
    await PatchEHRPoster({
      resourceType: "DocumentReference",
      resourceId: documentReferenceId,
      operation: "replace",
      path: "/attester",
      value: nextAttester,
      applicationAuth: true,
    });
  } else {
    const entriesToAdd: AttesterEntryLike[] = [];
    if (!hasPractitionerRoleAttester) {
      entriesToAdd.push(buildPractitionerRoleAttesterEntry(practitionerRoleId));
    }
    if (!hasOrganizationAttester) {
      entriesToAdd.push(organizationAttester);
    }

    const hasExistingAttester = currentAttester.length > 0;

    if (!hasExistingAttester && entriesToAdd.length > 1) {
      await PatchEHRPoster({
        resourceType: "DocumentReference",
        resourceId: documentReferenceId,
        operation: "add",
        path: "/attester",
        value: entriesToAdd,
        applicationAuth: true,
      });
    } else {
      for (const entry of entriesToAdd) {
        const hasAttesterNow =
          hasExistingAttester || entriesToAdd.indexOf(entry) > 0;
        await PatchEHRPoster({
          resourceType: "DocumentReference",
          resourceId: documentReferenceId,
          operation: "add",
          path: hasAttesterNow ? "/attester/-" : "/attester",
          value: hasAttesterNow ? entry : [entry],
          applicationAuth: true,
        });
      }
    }
  }

  await persistDocumentValidityExtensions(
    documentReferenceId,
    params.currentExtension,
    params.transactionId,
  );

  return { documentReferenceId, alreadyAttested };
}

/**
 * Dispara attestation das 4 categorias em paralelo com erro parcial:
 * ids que falharem voltam em `failed` para a UI decidir (toast/refetch).
 */
export async function attestAllSupportingDocuments(
  params: AttestAllSupportingDocumentsParams,
): Promise<AttestAllSupportingDocumentsResult> {
  const results = await Promise.allSettled(
    params.documents.map((document) =>
      attestSupportingDocument({
        documentReferenceId: document.documentReferenceId,
        organizationId: params.organizationId,
        practitionerRoleId: params.practitionerRoleId,
        currentAttester: document.currentAttester,
        currentExtension: document.currentExtension,
        validUntil: params.validUntil,
        transactionId: document.transactionId ?? params.transactionId,
      }),
    ),
  );

  const attested: string[] = [];
  const alreadyAttested: string[] = [];
  const failed: string[] = [];

  results.forEach((result, index) => {
    const documentReferenceId = params.documents[index]?.documentReferenceId;
    if (!documentReferenceId) return;

    if (result.status === "rejected") {
      failed.push(documentReferenceId);
      return;
    }
    if (result.value.alreadyAttested) {
      alreadyAttested.push(documentReferenceId);
      return;
    }
    attested.push(documentReferenceId);
  });

  return { attested, alreadyAttested, failed };
}

/**
 * Reprovação scoped por Organization (multi-loja).
 * NÃO altera docStatus global — evita invalidar o documento para outras orgs.
 * Grava extensions: reason + source (PractitionerRole) + rejection-organization.
 * Idempotente: se a org já reprovou, só reforça reason/source.
 */
export async function rejectSupportingDocument(
  params: RejectSupportingDocumentParams,
): Promise<RejectSupportingDocumentResult> {
  const documentReferenceId = params.documentReferenceId?.trim();
  const reason = params.reason?.trim();
  const practitionerRoleId = params.practitionerRoleId?.trim();
  const organizationId = params.organizationId?.trim();

  if (!documentReferenceId) {
    throw new Error("Documento não identificado para reprovação.");
  }
  if (!reason) {
    throw new Error("Informe o motivo da reprovação.");
  }
  if (!practitionerRoleId) {
    throw new Error("Vínculo profissional não identificado para reprovação.");
  }
  if (!organizationId) {
    throw new Error("Organização não identificada para reprovação.");
  }

  const hasExtension = Boolean(params.currentExtension?.length);
  const reasonExtension = {
    url: DOCUMENT_REJECTION_REASON_EXTENSION_URL,
    valueString: reason,
  };
  const sourceExtension = {
    url: DOCUMENT_REJECTION_SOURCE_EXTENSION_URL,
    valueString: buildProfessionalRejectionSourceValue(practitionerRoleId),
  };
  const organizationExtension = {
    url: DOCUMENT_REJECTION_ORGANIZATION_EXTENSION_URL,
    valueString: buildOrganizationRejectionReference(organizationId),
  };

  const alreadyRejectedByOrg = isDocumentRejectedByOrganization(
    { extension: params.currentExtension },
    organizationId,
  );

  const toAdd = alreadyRejectedByOrg
    ? [reasonExtension, sourceExtension]
    : [reasonExtension, sourceExtension, organizationExtension];

  if (hasExtension) {
    for (const ext of toAdd) {
      await PatchEHRPoster({
        resourceType: "DocumentReference",
        resourceId: documentReferenceId,
        operation: "add",
        path: "/extension/-",
        value: ext,
      });
    }
  } else {
    await PatchEHRPoster({
      resourceType: "DocumentReference",
      resourceId: documentReferenceId,
      operation: "add",
      path: "/extension",
      value: toAdd,
    });
  }

  return { documentReferenceId };
}

function isOrganizationAttesterEntry(
  entry: AttesterEntryLike,
  organizationId: string,
): boolean {
  return Boolean(findOrganizationAttester([entry], organizationId));
}

/**
 * Desfaz aprovação desta org: replace de `/attester` sem os attesters da org.
 * PatchEHRPoster só aceita add|replace — não usamos remove.
 */
export async function revokeDocumentApproval(
  params: RevokeDocumentApprovalParams,
): Promise<{ documentReferenceId: string }> {
  const documentReferenceId = params.documentReferenceId?.trim();
  const organizationId = params.organizationId?.trim();
  if (!documentReferenceId) {
    throw new Error("Documento não identificado para desfazer aprovação.");
  }
  if (!organizationId) {
    throw new Error("Organização não identificada para desfazer aprovação.");
  }

  const remaining = (params.currentAttester ?? []).filter(
    (entry) => !isOrganizationAttesterEntry(entry, organizationId),
  );

  await PatchEHRPoster({
    resourceType: "DocumentReference",
    resourceId: documentReferenceId,
    operation: "replace",
    path: "/attester",
    value: remaining,
    applicationAuth: true,
  });

  return { documentReferenceId };
}

function isThisOrgRejectionOrganizationExt(
  ext: DocumentExtensionLike,
  organizationId: string,
): boolean {
  if (ext.url !== DOCUMENT_REJECTION_ORGANIZATION_EXTENSION_URL) return false;
  const orgRef = buildOrganizationRejectionReference(organizationId);
  const value = ext.valueString?.trim() ?? "";
  return value === orgRef || value.includes(organizationId);
}

/**
 * Desfaz recusa desta org: replace de `/extension` sem as 3 extensions scoped.
 */
export async function revokeDocumentRejection(
  params: RevokeDocumentRejectionParams,
): Promise<{ documentReferenceId: string }> {
  const documentReferenceId = params.documentReferenceId?.trim();
  const organizationId = params.organizationId?.trim();
  if (!documentReferenceId) {
    throw new Error("Documento não identificado para desfazer recusa.");
  }
  if (!organizationId) {
    throw new Error("Organização não identificada para desfazer recusa.");
  }

  const current = params.currentExtension ?? [];
  const remaining = current.filter((ext) => {
    if (isThisOrgRejectionOrganizationExt(ext, organizationId)) return false;
    if (ext.url === DOCUMENT_REJECTION_REASON_EXTENSION_URL) return false;
    if (ext.url === DOCUMENT_REJECTION_SOURCE_EXTENSION_URL) return false;
    return true;
  });

  await PatchEHRPoster({
    resourceType: "DocumentReference",
    resourceId: documentReferenceId,
    operation: current.length ? "replace" : "add",
    path: "/extension",
    value: remaining,
    applicationAuth: true,
  });

  return { documentReferenceId };
}

export const documentAttestationService = {
  buildOrganizationAttesterEntry,
  buildPractitionerRoleAttesterEntry,
  buildProfessionalAttesterEntry,
  getDocumentStatusCode,
  getDocumentStatusText,
  isRejectedDocumentStatus,
  isCancelledDocumentStatus,
  extractRejectionReason,
  extractProfessionalRejectionSource,
  buildProfessionalRejectionSourceValue,
  extractDocumentVerification,
  isProfessionalRejectionDocument,
  hasProfessionalDocumentRejection,
  isDocumentRejectedByOrganization,
  isDocumentAttestedByOrganization,
  extractDocumentAttestation,
  extractOrganizationValidUntil,
  areAllSupportingDocumentsAttested,
  attestSupportingDocument,
  attestAllSupportingDocuments,
  rejectSupportingDocument,
  revokeDocumentApproval,
  revokeDocumentRejection,
  extractDocumentValidUntil,
  extractDocumentTransactionId,
  isDocumentValidityExpired,
  isLegacySupportingDocument,
  DOCUMENT_VALID_UNTIL_EXTENSION_URL,
  DOCUMENT_TRANSACTION_ID_EXTENSION_URL,
};
